/**
 * Admin → Gambling Ledger.
 *
 * Per-bet audit view of the `gambling_log` table. Single user filter
 * (gambling is one-sided), plus dropdowns for game type and win/loss.
 * Surfaces all-time biggest win + loss for the current filter so the
 * admin sees outliers instantly.
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Field } from "@/components/Atoms";
import { UserSearchInput } from "@/components/admin/UserSearchInput";
import { Dice5, RefreshCw, X } from "lucide-react";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";

const GAMES = [
  { id: "", label: "All games" },
  { id: "coinflip", label: "Coinflip" },
  { id: "slots", label: "Slots" },
  { id: "blackjack", label: "Blackjack" },
];

const OUTCOMES = [
  { id: "", label: "All outcomes" },
  { id: "true", label: "Wins only" },
  { id: "false", label: "Losses only" },
];

export function GamblingLedger() {
  const toast = useToast();
  const [userId, setUserId] = useState("");
  const [game, setGame] = useState("");
  const [won, setWon] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [biggestWin, setBiggestWin] = useState(0);
  const [biggestLoss, setBiggestLoss] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (userId) params.set("user_id", userId);
      if (game) params.set("game", game);
      if (won !== "") params.set("won", won);
      const { data } = await api.get(`/admin/gambling/log?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setBiggestWin(data.biggest_win || 0);
      setBiggestLoss(data.biggest_loss || 0);
    } catch (e) {
      toast.error(apiError(e, "Couldn't load gambling ledger"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, game, won]);

  const clearAll = () => { setUserId(""); setGame(""); setWon(""); };

  return (
    <div className="space-y-4 fade-up" data-testid="gambling-ledger-tab">
      <div className="glass-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Dice5 size={16} className="text-[var(--teal)]" />
            <h3 className="text-lg" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
              Gambling Ledger
            </h3>
          </div>
          <div className="text-xs text-[var(--muted)]">
            {loading ? "Loading…" : `${items.length} shown · ${total.toLocaleString()} total bets`}
          </div>
        </div>
        <p className="text-xs text-[var(--muted)] mt-1">
          Every coinflip / slots / blackjack roll is logged here with bet, payout, and net P&amp;L for audit.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="glass-card p-4">
          <div className="label">Biggest Win (filter)</div>
          <div className="text-2xl glow-gold mt-1" data-testid="gambling-ledger-biggest-win">
            +{biggestWin.toLocaleString()} <ChilletCoinIcon size={14}/>
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="label">Biggest Loss (filter)</div>
          <div className="text-2xl text-[var(--danger)] mt-1" data-testid="gambling-ledger-biggest-loss">
            {biggestLoss.toLocaleString()} <ChilletCoinIcon size={14}/>
          </div>
        </div>
        <div className="glass-card p-4 hidden md:block">
          <div className="label">Filtered Total Bets</div>
          <div className="text-2xl glow-teal mt-1">{total.toLocaleString()}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
        <Field label="User">
          <UserSearchInput
            value={userId}
            onChange={setUserId}
            onPick={(row) => setUserId(row.discord_id || "")}
            scope="discord_id"
            placeholder="Search Discord/in-game name or paste a user_id…"
            dataTestid="gambling-ledger-user-search"
          />
        </Field>
        <Field label="Game">
          <select
            className="input"
            value={game}
            onChange={(e) => setGame(e.target.value)}
            data-testid="gambling-ledger-game"
          >
            {GAMES.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
        </Field>
        <Field label="Outcome">
          <select
            className="input"
            value={won}
            onChange={(e) => setWon(e.target.value)}
            data-testid="gambling-ledger-outcome"
          >
            {OUTCOMES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <button
          onClick={clearAll}
          className="btn btn-sm btn-secondary"
          disabled={!userId && !game && won === ""}
          data-testid="gambling-ledger-clear"
        >
          <X size={12} /> Clear
        </button>
      </div>

      <div className="glass-card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
          <span className="label !mb-0">Recent</span>
          <button
            onClick={load}
            className="btn btn-sm btn-secondary"
            disabled={loading}
            data-testid="gambling-ledger-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto max-h-[60dvh]">
          <table className="w-full min-w-[680px] text-sm" data-testid="gambling-ledger-table">
            <thead className="sticky top-0 bg-[#0c0510]/95 backdrop-blur-sm">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
                <th className="px-4 py-2 w-44">When</th>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Game</th>
                <th className="px-4 py-2 text-right">Bet</th>
                <th className="px-4 py-2 text-right">Payout</th>
                <th className="px-4 py-2 text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`gambling-log-row-${r.id}`}>
                  <td className="px-4 py-2 text-xs text-[var(--muted)] font-mono whitespace-nowrap">
                    {fmtTime(r.created_at)}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => setUserId(r.user_id)}
                      className="text-left text-[10px] font-mono text-[var(--muted)] hover:text-[var(--teal)] transition-colors truncate max-w-[220px] block"
                      title="Filter to this user"
                    >
                      {r.user_id}
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <span className="badge text-[var(--muted)] border-white/10 capitalize">{r.game}</span>
                  </td>
                  <td className="px-4 py-2 text-right text-[var(--muted)] whitespace-nowrap">
                    {r.bet.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {r.payout > 0 ? <span className="inline-flex items-center gap-1">{r.payout.toLocaleString()} <ChilletCoinIcon size={12}/></span> : <span className="text-[var(--muted)]">—</span>}
                  </td>
                  <td className={`px-4 py-2 text-right whitespace-nowrap ${r.net > 0 ? "glow-gold" : r.net < 0 ? "text-[var(--danger)]" : ""}`}>
                    {r.net > 0 ? "+" : ""}{r.net.toLocaleString()}
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-[var(--muted)] text-sm">
                    {(userId || game || won !== "")
                      ? "No bets match the current filters."
                      : "No gambling rolls recorded yet — fills in as players use the Gambling Den."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "2-digit", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}
